import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchDashboard } from '../lib/dashboard'
import type { DashboardSnapshot } from '../lib/dashboard'

export const DASHBOARD_POLL_MS = 10_000

export function dashboardNeedsPoll(data: DashboardSnapshot): boolean {
  return data.queue.length > 0
    || data.recent.some((job) => job.thumbnail_state === 'pending')
    || data.failed_24h.some((job) => job.refund_confirmed !== true)
    || data.music_attention.some((item) => item.signal === 'analysis_pending' || item.signal === 'analysis_auto')
}

/**
 * Sesja 11 (G3-shell-dashboard-ds-31): the screen shows a FRESHNESS MARK, not a promise of
 * an interval. `readAt` is the moment the last snapshot landed; `nextReadAt` is set only
 * while a poll is really scheduled (the four conditions above, or a watched retry), so the
 * countdown under „Co się teraz dzieje” is true by construction. A failed read keeps the
 * previous `data` and `readAt`, and sets `error` — the mark turns amber, not blank.
 */
export function useDashboard() {
  const [data, setData] = useState<DashboardSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [readAt, setReadAt] = useState<Date | null>(null)
  const [nextReadAt, setNextReadAt] = useState<Date | null>(null)
  const [nonce, setNonce] = useState(0)
  const retryPending = useRef(new Set<number>())
  const refetch = useCallback(() => setNonce((n) => n + 1), [])
  const watchRetry = useCallback((trackId: number) => {
    retryPending.current.add(trackId)
    setNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = () => {
      timer = setTimeout(tick, DASHBOARD_POLL_MS)
      setNextReadAt(new Date(Date.now() + DASHBOARD_POLL_MS))
    }
    const tick = async () => {
      try {
        const next = await fetchDashboard()
        if (!alive) return
        setData(next)
        setError(null)
        setLoading(false)
        setReadAt(new Date())
        for (const trackId of retryPending.current) {
          const item = next.music_attention.find((candidate) => candidate.id === trackId)
          if (!item || !['analysis_manual', 'analysis_beats'].includes(item.signal)) {
            retryPending.current.delete(trackId)
          }
        }
        if (dashboardNeedsPoll(next) || retryPending.current.size > 0) schedule()
        else setNextReadAt(null)
      } catch (cause) {
        if (!alive) return
        setError(cause instanceof Error ? cause : new Error(String(cause)))
        setLoading(false)
        if (retryPending.current.size > 0) schedule()
        else setNextReadAt(null)
      }
    }
    setLoading(true)
    void tick()
    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [nonce])

  return { data, loading, error, readAt, nextReadAt, refetch, watchRetry }
}
